import { Monitoring } from '@outbound-ai/softphone'
import { useAppSelector } from 'appState/store'
import { apiPaths } from 'configs/apiConfigs'
import { env } from 'configs/env'
import { authApi } from 'features/auth/authApi'
import { ClaimMinimizedCallModal } from 'features/calls/components'
import { CallCompletionNotifier } from 'features/calls/components/CallCompletionNotifier'
import { softphone } from 'features/calls/utils/softphone'
import { getMaintenanceNotification } from 'features/claims/claimsApi'
import { ClaimCallJobPayorAgentReadyModal } from 'features/claims/components'
import { MaintenanceNotification } from 'features/claims/types'
import {
  isNotificationActive,
  sortNotificationsByDate,
} from 'features/MaintenanceNotification/Internal/NotificationHelper'
import { MaintenanceNotificationModel } from 'features/MaintenanceNotification/MaintenanceNotificationModel'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IChildrenNode } from 'types/propTypes'
import { MAINTENANCE_STORAGE_KEY } from 'utils/constants'

import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'

import { Copyright } from './Copyright'
import { Navbar } from './Navbar'

interface IProps extends IChildrenNode {
  className?: string
}

export const Layout = ({ children, className }: IProps) => {
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const roleIds = profile?.roles.map((role) => role.roleId)
  const isClaimAccessPermission = roleIds?.includes(IUserRoleGuidEnum.claimAccessPermission)
  const tenants = profile?.tenants || []

  const [isAuthStatus, setIsAuthStatus] = useState(false)

  const [isActiveNotification, setActiveNotification] = useState(false)
  const [maintenanceNotificationData, setMaintenanceNotificationData] = useState<MaintenanceNotification[] | []>([])

  const isNotificationSeen = localStorage.getItem(MAINTENANCE_STORAGE_KEY)

  useEffect(() => {
    handleMaintenanceNotification()
  }, [])

  useEffect(() => {
    // Set auth status based on profile and permissions
    if (profile != undefined && isClaimAccessPermission && tenants.length > 0) {
      setIsAuthStatus(true)
    } else {
      setIsAuthStatus(false)
    }
  }, [profile, isClaimAccessPermission, tenants])

  useEffect(() => {
    const resumeNotifications = async () => {
      await softphone.resumeAudioContext()
    }

    document.addEventListener('click', resumeNotifications, { once: true })

    return () => {
      document.removeEventListener('click', resumeNotifications)
    }
  }, [])

  useEffect(() => {
    (async () => {
      Monitoring.initWebSocketLatencyTest(env.VITE_CALLS_SERVICE_WSS || '', await authApi.getAuthToken())
    })()
  }, [])

  async function handleMaintenanceNotification() {
    const query = apiPaths.maintenanceNotification()

    try {
      const allNotifications: MaintenanceNotification[] = await getMaintenanceNotification(query)

      if (!Array.isArray(allNotifications)) {
        console.error('[Maintenance] Expected an array, got:', allNotifications)
        return
      }

      const now = new Date()

      const activeNotifications = allNotifications.filter((item) => {
        const active = isNotificationActive(item.startOn, item.endOn, now)
        const allowedByPreLogin = item.isPreLogin === false
        return active && allowedByPreLogin
      })
      const sortedActiveNotifications = sortNotificationsByDate(activeNotifications)
      if (sortedActiveNotifications.length > 0) {
        setMaintenanceNotificationData(sortedActiveNotifications)
        setActiveNotification(true)
      } else {
        setMaintenanceNotificationData([])
        setActiveNotification(false)
      }
    } catch (error) {
      console.error(' Failed to fetch notifications:', error)
    }
  }

  return (
    <StyleLayout className={className}>
      <Navbar />
      {isActiveNotification && !isNotificationSeen && maintenanceNotificationData.length > 0 && (
        <MaintenanceNotificationModel
          showModal={isActiveNotification}
          notificationData={maintenanceNotificationData}
          setNotificationClose={(notification: boolean) => {
            localStorage.setItem(MAINTENANCE_STORAGE_KEY, 'true')
            setActiveNotification(notification)
          }}
        />
      )}

      <main>{children}</main>
      <Copyright />
      {isAuthStatus ? <CallCompletionNotifier /> : null}

      <ClaimMinimizedCallModal />
      <ClaimCallJobPayorAgentReadyModal />
    </StyleLayout>
  )
}

export const StyleLayout = styled.div`
  height: 100vh;

  > main {
    height: calc(100vh - ${({ theme }) => theme.constants.navbarHeight} - 2rem);
    margin-top: ${({ theme }) => theme.constants.navbarHeight};
    background: ${({ theme }) => theme.colors.neutral.lightest};
  }
`
